import { yupResolver } from "@hookform/resolvers/yup";
import Grid from "@mui/material/Grid";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";

import { useAuth } from "@/features/authentication";
import { CenteredLoading } from "@/features/ui";

import { GeneralInformationSection } from "./general-information-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./other-payment-form-constants";
import { OtherPaymentDetailsSection } from "./other-payment-details-section";

export const OtherPaymentForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  isLoading = false,
}) => {
  const { outlet } = useAuth();

  const methods = useForm({
    defaultValues: defaultValues
      ? { ...DEFAULT_VALUES, ...defaultValues }
      : DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const { handleSubmit, formState } = methods;

  const onFormSubmit = async (formData) => {
    const payload = {
      // The page lifts this out and sends it as the Outlet-Id header.
      outletId: outlet?.id,
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      cashBankOutletChartOfAccountId:
        formData.cashBankOutletChartOfAccount.id,
      checkNumber: formData.checkNumber || null,
      payee: formData.payee || null,
      note: formData.note || null,
      // Full replace: every line is sent every time, so one left out is deleted.
      otherPaymentDetails: formData.otherPaymentDetails.map((line) => ({
        outletChartOfAccountId: line.outletChartOfAccount.id,
        amount: Number(line.amount),
        note: line.note || null,
        // Both null on a hand-typed line; both set on a line fetched via Ambil.
        sourceableType: line.sourceableType || null,
        sourceableId: line.sourceableId || null,
      })),
    };

    await onSubmit(payload);
  };

  if (isLoading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  return (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
        spacing={2.5}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <OtherPaymentDetailsSection />
        </Grid>

        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

OtherPaymentForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
